import React, { useState, useEffect, useCallback } from "react";
import _ from "lodash";
import { getMonthlyRpt } from "../../modules/ReportsManager";
import { showSpinner, closeSpinner, modal, alert } from "../../modules/Dialogs";
import { sessionExpired, userType } from "../../config/GeneralConfig";
import { toScreen } from "../../modules/Routing";
import PropTypes from "prop-types";
import withNavigate from "../withNavigate";
import RptMonthlyCharts from "./RptMonthlyCharts";
import { monthNames } from "../../../utils/constants";

const RptMonthly = ({ userDetails, navigate }) => {
  const currentYear = new Date().getFullYear();
  const [crYear, setCrYear] = useState(currentYear);
  const [data, setData] = useState({});
  const [showCharts, setShowCharts] = useState(false);
  const [viewContainer, setViewContainer] = useState(null);
  const [showYearCalendar, setShowYearCalendar] = useState(false);
  const [decadeStart, setDecadeStart] = useState(
    Math.floor(currentYear / 10) * 10,
  );
  const [selectedMetric, setSelectedMetric] = useState("jobCount");
  const [chartType, setChartType] = useState("bar");
  const yearPickerRef = React.useRef(null);

  const getReportData = useCallback(
    (year, callback) => {
      const queryParams = {
        userType: "mav",
        theYear: year,
      };
      getMonthlyRpt(queryParams, callback, userDetails.jwttoken);
    },
    [userDetails.jwttoken],
  );

  const getReport = useCallback(
    (event) => {
      if (event) event.preventDefault();
      showSpinner();

      let completed = 0;
      const tempData = {};

      const handleCurrentYear = (resStatus, responseString, resJson) => {
        if (resStatus === 200) {
          tempData.currentYear = resJson?.data;
        } else if (resStatus === 401) {
          modal(sessionExpired);
          toScreen(false, navigate, "/logout");
        } else if (resStatus === 400) {
          alert(responseString);
        }
        completed++;
        if (completed === 2) {
          closeSpinner();
          setData(tempData);
        }
      };

      const handlePreviousYear = (resStatus, responseString, resJson) => {
        if (resStatus === 200) {
          tempData.previousYear = resJson?.data;
        } else if (resStatus === 401) {
          modal(sessionExpired);
          toScreen(false, navigate, "/logout");
        } else if (resStatus === 400) {
          alert(responseString);
        }
        completed++;
        if (completed === 2) {
          closeSpinner();
          setData(tempData);
        }
      };

      getReportData(crYear, handleCurrentYear);
      getReportData(crYear - 1, handlePreviousYear);
    },
    [crYear, getReportData, navigate],
  );

  useEffect(() => {
    if (crYear) {
      getReport();
    }
  }, [crYear]);

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (
        yearPickerRef.current &&
        !yearPickerRef.current.contains(event.target)
      ) {
        setShowYearCalendar(false);
      }
    };

    if (showYearCalendar) {
      document.addEventListener("mousedown", handleClickOutside);
      return () => {
        document.removeEventListener("mousedown", handleClickOutside);
      };
    }
  }, [showYearCalendar]);

  const handleYearInputClick = () => {
    setShowYearCalendar(!showYearCalendar);
  };

  const handleYearSelect = (year) => {
    setCrYear(year);
    setShowYearCalendar(false);
  };

  const handlePrevDecade = () => {
    setDecadeStart(decadeStart - 10);
  };

  const handleNextDecade = () => {
    setDecadeStart(decadeStart + 10);
  };

  const getYearGridData = () => {
    const years = [];
    for (let i = 0; i < 12; i++) {
      const year = decadeStart + i;
      if (year <= currentYear) {
        years.push(year);
      }
    }
    return years;
  };

  const handleRefreshClick = () => {
    getReport();
  };

  const calculateTotals = () => {
    let curr = {
      driverCount: 0,
      userCount: 0,
      jobCount: 0,
      totalQuotes: 0,
      notQuoted: 0,
      booked: 0,
      refunded: 0,
      jobTotalRatings: 0,
      bookedPercentage: [],
    };
    let prev = {
      driverCount: 0,
      userCount: 0,
      jobCount: 0,
      totalQuotes: 0,
      notQuoted: 0,
      booked: 0,
      refunded: 0,
      jobTotalRatings: 0,
      bookedPercentage: [],
    };

    Object.entries(data.currentYear || {}).forEach(([key, row]) => {
      curr.driverCount += row.driverCount || 0;
      curr.userCount += row.userCount || 0;
      curr.jobCount += row.jobCount || 0;
      curr.totalQuotes += row.totalQuotes || 0;
      curr.notQuoted += row.notQuoted || 0;
      curr.booked += row.booked || 0;
      curr.refunded += row.refunded || 0;
      curr.jobTotalRatings += row.jobTotalRatings || 0;
      if (row.bookedPercentage)
        curr.bookedPercentage.push(row.bookedPercentage);
    });

    Object.entries(data.previousYear || {}).forEach(([key, row]) => {
      prev.driverCount += row.driverCount || 0;
      prev.userCount += row.userCount || 0;
      prev.jobCount += row.jobCount || 0;
      prev.totalQuotes += row.totalQuotes || 0;
      prev.notQuoted += row.notQuoted || 0;
      prev.booked += row.booked || 0;
      prev.refunded += row.refunded || 0;
      prev.jobTotalRatings += row.jobTotalRatings || 0;
      if (row.bookedPercentage)
        prev.bookedPercentage.push(row.bookedPercentage);
    });

    const currAvgConv =
      curr.bookedPercentage.length > 0
        ? (
            curr.bookedPercentage.reduce((a, b) => a + b) /
            curr.bookedPercentage.length
          ).toFixed(2)
        : 0;
    const prevAvgConv =
      prev.bookedPercentage.length > 0
        ? (
            prev.bookedPercentage.reduce((a, b) => a + b) /
            prev.bookedPercentage.length
          ).toFixed(2)
        : 0;

    return {
      drivers: { curr: curr.driverCount, prev: prev.driverCount },
      users: { curr: curr.userCount, prev: prev.userCount },
      jobCount: { curr: curr.jobCount, prev: prev.jobCount },
      totalQuotes: { curr: curr.totalQuotes, prev: prev.totalQuotes },
      notQuoted: { curr: curr.notQuoted, prev: prev.notQuoted },
      booked: { curr: curr.booked, prev: prev.booked },
      refunded: { curr: curr.refunded, prev: prev.refunded },
      totalReviews: { curr: curr.jobTotalRatings, prev: prev.jobTotalRatings },
      convRate: { curr: currAvgConv, prev: prevAvgConv },
    };
  };

  const getGrowthPercent = (curr, prev) => {
    if (prev === 0) return { pct: "0", isPositive: false };
    const pct = (((curr - prev) / prev) * 100).toFixed(1);
    return { pct, isPositive: parseFloat(pct) >= 0 };
  };

  const getMonthlyGrowthPercent = (currVal, prevVal, isInverse = false) => {
    if (prevVal === 0) {
      if (currVal > 0) return { pct: "100", isPositive: isInverse ? false : true };
      if (currVal === 0) return { pct: "0", isPositive: true };
    }
    const pct = (((currVal - prevVal) / prevVal) * 100).toFixed(1);
    const pctValue = parseFloat(pct);
    const isPositive = isInverse ? pctValue <= 0 : pctValue >= 0;
    return { pct: Math.abs(pct), isPositive };
  };

  const renderReport = (monthNum) => {
    const zeroIndexedMonth = monthNum - 1;
    const now = new Date();
    const currentMonth = now.getMonth();
    const isFutureMonth = crYear === currentYear && monthNum - 1 > currentMonth;

    const currRow =
      data.currentYear?.[zeroIndexedMonth] ||
      data.currentYear?.[String(zeroIndexedMonth)] ||
      data.currentYear?.[monthNum] ||
      data.currentYear?.[String(monthNum)] ||
      {};

    const prevRow =
      data.previousYear?.[zeroIndexedMonth] ||
      data.previousYear?.[String(zeroIndexedMonth)] ||
      data.previousYear?.[monthNum] ||
      data.previousYear?.[String(monthNum)] ||
      {};

    const metrics = [
      {
        key: "driverCount",
        curr: currRow?.driverCount || 0,
        prev: prevRow?.driverCount || 0,
        isInverse: false,
      },
      {
        key: "userCount",
        curr: currRow?.userCount || 0,
        prev: prevRow?.userCount || 0,
        isInverse: false,
      },
      {
        key: "jobCount",
        curr: currRow?.jobCount || 0,
        prev: prevRow?.jobCount || 0,
        isInverse: false,
      },
      {
        key: "totalQuotes",
        curr: currRow?.totalQuotes || 0,
        prev: prevRow?.totalQuotes || 0,
        isInverse: false,
      },
      {
        key: "notQuoted",
        curr: currRow?.notQuoted || 0,
        prev: prevRow?.notQuoted || 0,
        isInverse: true,
      },
      {
        key: "booked",
        curr: currRow?.booked || 0,
        prev: prevRow?.booked || 0,
        isInverse: false,
      },
      {
        key: "refunded",
        curr: currRow?.refunded || 0,
        prev: prevRow?.refunded || 0,
        isInverse: true,
      },
      {
        key: "jobTotalRatings",
        curr: currRow?.jobTotalRatings || 0,
        prev: prevRow?.jobTotalRatings || 0,
        isInverse: false,
      },
      {
        key: "bookedPercentage",
        curr: currRow?.bookedPercentage?.toFixed(0) || 0,
        prev: prevRow?.bookedPercentage?.toFixed(0) || 0,
        isInverse: false,
      },
    ];

    const hasCurrentYearData = metrics.some((m) => m.curr > 0);
    const shouldShowGrowth = hasCurrentYearData && !isFutureMonth;

    // Show row even if both are null (display 0 values)
    return (
      <React.Fragment key={monthNum}>
        <tr className="prev-year-row">
          <td className="month-cell" rowSpan={shouldShowGrowth ? 3 : 2}>
            {monthNames[monthNum - 1]}
          </td>
          <td className="year-badge prev-year-badge">{crYear - 1}</td>
          {metrics.map((m) => (
            <td key={`${monthNum}-prev-${m.key}`}>{m.prev}</td>
          ))}
        </tr>
        <tr className="curr-year-row">
          <td className="year-badge curr-year-badge">{crYear}</td>
          {metrics.map((m) => (
            <td key={`${monthNum}-curr-${m.key}`}>{m.curr}</td>
          ))}
        </tr>
        {shouldShowGrowth && (
          <tr className="growth-row">
            <td className="growth-label">Growth</td>
            {metrics.map((m) => {
              const growth = getMonthlyGrowthPercent(m.curr, m.prev, m.isInverse);
              return (
                <td
                  key={`${monthNum}-growth-${m.key}`}
                  className={`growth-cell ${growth.isPositive ? "positive" : "negative"}`}
                >
                  <span className="growth-indicator">
                    <span className="arrow">
                      {growth.isPositive ? "▲" : "▼"}
                    </span>
                    <span className="pct">{growth.pct}%</span>
                  </span>
                </td>
              );
            })}
          </tr>
        )}
      </React.Fragment>
    );
  };

  return (
    <div className="l-base-layout__content">
      <h1>Admin / Reports / Monthly report</h1>
      <div className="c-hr"></div>
      <div className="l-row">
        <div className="l-col-100 u-padding-top-0 u-padding-bottom-0">
          <div className="c-filter-bar-2">
            <form>
              <div className="item-container">
                <label className="item-label">Compare Year:</label>
                <div className="year-input-wrapper" ref={yearPickerRef}>
                  <button
                    type="button"
                    className="year-input-button"
                    onClick={handleYearInputClick}
                  >
                   {crYear - 1} vs {crYear}
                    <svg
                      width="16"
                      height="16"
                      viewBox="0 0 24 24"
                      fill="none"
                      stroke="currentColor"
                      strokeWidth="2"
                    >
                      <polyline points="6 9 12 15 18 9" />
                    </svg>
                  </button>
                  {showYearCalendar && (
                    <div className="year-calendar-picker">
                      <div className="calendar-header">
                        <button
                          type="button"
                          className="nav-button prev"
                          onClick={handlePrevDecade}
                        >
                          ←
                        </button>
                        <span className="decade-display">
                          {decadeStart}-{Math.min(decadeStart + 9, currentYear)}
                        </span>
                        <button
                          type="button"
                          className="nav-button next"
                          onClick={handleNextDecade}
                          disabled={decadeStart + 10 > currentYear}
                        >
                          →
                        </button>
                      </div>
                      <div className="year-grid">
                        {getYearGridData().map((year) => (
                          <button
                            key={year}
                            type="button"
                            className={`year-cell ${year === crYear ? "selected" : ""}`}
                            onClick={() => handleYearSelect(year)}
                          >
                            {year}
                          </button>
                        ))}
                      </div>
                    </div>
                  )}
                </div>
                <button
                  type="button"
                  onClick={handleRefreshClick}
                  className="search-button"
                >
                  Refresh
                </button>
                <div className="view-buttons-group">
                  <button
                    type="button"
                    onClick={() => setShowCharts(false)}
                    className={`view-button ${!showCharts ? "active" : ""}`}
                  >
                    View Table
                  </button>
                  <button
                    type="button"
                    onClick={() => setShowCharts(true)}
                    className={`view-button ${showCharts ? "active" : ""}`}
                  >
                    View Charts
                  </button>
                </div>
              </div>
            </form>
          </div>
        </div>
        <div className="l-col-100" ref={setViewContainer}>
          {showCharts ? (
            <div className="charts-view">
              {Object.keys(data.currentYear || {}).length > 0 ||
              Object.keys(data.previousYear || {}).length > 0 ? (
                <RptMonthlyCharts
                  currentYearData={data.currentYear || {}}
                  previousYearData={data.previousYear || {}}
                  currentYear={crYear}
                  selectedMetric={selectedMetric}
                  setSelectedMetric={setSelectedMetric}
                  chartType={chartType}
                  setChartType={setChartType}
                />
              ) : (
                <p className="text-danger u-text-align-center my-16">
                  No records found.
                </p>
              )}
            </div>
          ) : (
            <>
              <div className="monthly-table-wrapper">
                <table className="monthly-report-table">
                  <thead>
                    <tr>
                      <th>Month</th>
                      <th>Year</th>
                      <th>Drivers</th>
                      <th>Users</th>
                      <th>Jobs</th>
                      <th>Quotes</th>
                      <th>Not Quoted</th>
                      <th>Booked</th>
                      <th>Refunds</th>
                      <th>Total Reviews</th>
                      <th>Conversion Rate</th>
                    </tr>
                  </thead>
                  <tbody>
                    {Object.keys(data.currentYear || {}).length > 0 ||
                    Object.keys(data.previousYear || {}).length > 0 ? (
                      Array.from({ length: 12 }, (_, i) => renderReport(i + 1))
                    ) : (
                      <tr>
                        <td
                          colSpan="11"
                          className="text-danger u-text-align-center"
                        >
                          No records found.
                        </td>
                      </tr>
                    )}
                  </tbody>
                </table>
              </div>
            </>
          )}
        </div>
      </div>
    </div>
  );
};

RptMonthly.propTypes = {
  userDetails: PropTypes.object.isRequired,
  navigate: PropTypes.func.isRequired,
};

export default withNavigate(RptMonthly);
